<template>
  <div class="container main-container" key="filters">
    <div class="row">
      <filter-modal :before-close="filterModalBeforeClose" :on-confirm="onFilterModalOkClick"
                    :show="filterModalVisible"
                    :filterOptions="getFilterTypeOptions()" :current-form="currentForm"
                    :on-back="onFilterModalBackClick" :on-cancel="onFilterModalCancelClick"/>
      <warning-modal :show="warningModalVisible" :event-count="eventCount" :on-confirm="onWarningModalOkClick"
                     :on-cancel="onWarningModalCancelClick"/>
      <transition name="fade" mode="out-in">
        <div class="container" v-if="exportInProgress || countCheckInProgress" key="loadingSpinnerContainer">
          <div class="row" v-if="!countCheckInProgress && topForm.values.getAllPages && !isRunningInTableau">
            <div class="col-lg-6 col-md-6 col-lg-offset-3 col-md-offset-3">
              <alert type="warning">
                You can <a href="#" v-on:click="onCancelClick">stop this export</a> at any time and
                you'll get the events that have been downloaded so far.
              </alert>
            </div>
          </div>
          <div class="row text-center loading-row">
            <div class="col-lg-12">
              <spinner/>
            </div>
          </div>
        </div>
        <div class="container" v-if="token && !exportInProgress && !countCheckInProgress" key="mainContainer">
          <div class="row">
            <div class="col-lg-6 col-md-6 col-lg-offset-3 col-md-offset-3">
              <alert v-if="gotAPIError && errorMessage" type="danger">
                {{ errorMessage }}
              </alert>
              <alert v-if="gotAPIError && !errorMessage" type="danger">
                Oh no! Looks like there is something wrong with your filters. Please change them and try again.
              </alert>
              <alert v-if="gotOverflowError" type="danger">
                Sorry, this request is too large.
                Please make your filters more specific to reduce the number of results.
              </alert>
              <alert v-if="showDateFilterWarning" type="danger">
                Please specify date range using either Start, End or Active date filters.
              </alert>
            </div>
          </div>
          <top-form :values="topForm.values"/>
          <filter-table :filters="this.filters" :on-edit="onEditFilterClick" :on-delete="onDeleteFilterClick"/>
          <filter-table-footer :on-get-data="onGetDataClick" :on-add-filter="addNewFilter"
                               :disable-get-data="exportInProgress"/>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
  import Vue from 'vue'
  import * as R from 'ramda'
  import router from '@/router'

  import topForm from '@/components/wdc/table/top-form'
  import filterTable from '@/components/wdc/table/filter-table'
  import filterTableFooter from '@/components/wdc/table/filter-table-footer'
  import filterModal from '@/components/wdc/table/filter-modal'
  import warningModal from '@/components/wdc/table/warning-modal'

  import { formatFilters } from '@/app/wdc/form/formatters'
  import {
    ADDON_ACCESS,
    FILTER_OPTIONS,
    FILTER_DEFAULTS,
    SINGLE_FILTERS,
    DATE_FILTERS,
    EVENT_COUNT_WARNING_THRESHOLD
  } from '@/app/wdc/form/fixtures'
  import { validateFields } from '@/app/wdc/form/validators'
  import { WDCWrapper } from '@/app/wdc/wdc_wrapper'
  import spinner from '@/components/shared/spinner'
  import EventsAPI from '@/app/api/phq-public/events'
  import { cleanApiParams } from '@/app/utils'

  export default {
    components: {
      topForm,
      warningModal,
      filterTable,
      filterModal,
      filterTableFooter,
      spinner
    },
    name: 'filterIndex',
    data () {
      return {
        sortingOptions: [],
        exportInProgress: false,
        gotAPIError: false,
        gotOverflowError: false,
        showDateFilterWarning: false,
        topForm: {
          values: {
            limit: 500,
            getAllPages: true,
            sorts: []
          },
          type: 'topForm'
        },
        currentForm: {
          type: 'filterSelect',
          errors: {},
          values: {}
        },
        currentDateFilter: null,
        eventCount: 0,
        countCheckInProgress: false,
        warningModalVisible: false,
        filterModalVisible: false,
        filters: [],
        token: Vue.config.token,
        fields: [
          {
            label: 'Type',
            key: 'typeName'
          },
          {
            label: 'Parameters',
            key: 'params'
          },
          {
            label: 'Actions',
            key: 'actions'
          }
        ],
        errorMessage: null
      }
    },
    beforeCreate () {
      if (!Vue.config.token) {
        router.push('/')
      }
    },
    methods: {
      getFilterTypeOptions () {
        let currentFilters = this.filters.map(filter => filter.type)
        return FILTER_OPTIONS.filterTypes.filter(filterType => {
          // either place or within
          if (filterType.value === 'place' && currentFilters.includes('within')) {
            return false
          } else if (filterType.value === 'within' && currentFilters.includes('place')) {
            return false
          }
          // allowing only one type of date filter
          if (DATE_FILTERS.includes(filterType.value) && this.currentDateFilter) {
            return false
          }

          if (SINGLE_FILTERS.includes(filterType.value)) {
            if (!currentFilters.includes(filterType.value)) {
              return true
            }
          } else {
            return true
          }
        })
      },
      resetCurrentForm () {
        this.currentForm = {
          type: 'filterSelect',
          values: {},
          errors: {}
        }
      },
      addNewFilter () {
        this.filterModalVisible = true
        this.resetCurrentForm()
      },
      onEditFilterClick (filter, index) {
        filter.index = index
        filter.editing = true
        this.currentForm = filter
        this.filterModalVisible = true
      },
      onDeleteFilterClick (index) {
        if (DATE_FILTERS.includes(this.filters[index].type)) {
          this.currentDateFilter = null
        }
        this.filters.splice(index, 1)
      },
      onFilterModalOkClick () {
        const formType = this.currentForm.type
        let errors = validateFields(this.currentForm)
        this.currentForm.errors = errors
        if (Object.keys(errors).length === 0) {
          if (formType === 'filterSelect') {
            const filterFormType = this.currentForm.values.type.value
            this.currentForm = {
              type: filterFormType,
              values: FILTER_DEFAULTS[filterFormType] || {},
              typeName: this.currentForm.values.type.label,
              errors: {}
            }
          } else {
            this.filterModalVisible = false
            if (this.currentForm.editing) {
              this.currentForm.editing = false
              this.filters[this.currentForm.index] = this.currentForm
            } else {
              if (DATE_FILTERS.includes(this.currentForm.type)) {
                this.currentDateFilter = Object.assign({}, this.currentForm)
              }
              this.filters.push(this.currentForm)
              this.resetCurrentForm()
            }
          }
        } else {
          return false
        }
      },
      filterModalBeforeClose () {
        if (this.currentForm.editing) {
          return this.onFilterModalOkClick()
        }
        return true
      },
      onFilterModalCancelClick () {
        this.filterModalVisible = false
        this.resetCurrentForm()
      },
      onFilterModalBackClick () {
        this.resetCurrentForm()
      },
      triggerExport (params) {
        this.onCountCheckEnd()
        // forcing sorting
        params['sort'] = 'start'
        this.triggerTableauExport(params)
      },
      onGetDataClick () {
        let params = this._getParams()
        this.errorMessage = null
        params.token = this.token
        if (this.currentDateFilter) {
          this.showDateFilterWarning = false
          if (params.getAllPages) {
            this.getEventCount(params).then(count => {
              if (count > EVENT_COUNT_WARNING_THRESHOLD) {
                this.showWarningModal(count)
              } else {
                this.triggerExport(params)
              }
            }).catch(error => {
              this.onCountCheckEnd()
              this.onAPIError(error)
            })
          } else {
            this.triggerExport(params)
          }
        } else {
          this.showDateFilterWarning = true
        }
      },
      triggerTableauExport (params) {
        WDCWrapper.submitData(this.currentDateFilter, params, ADDON_ACCESS.aviation_rank, ADDON_ACCESS.phq_attendance)
      },
      showWarningModal (count) {
        this.eventCount = count
        this.warningModalVisible = true
      },
      onWarningModalOkClick () {
        this.warningModalVisible = false
        let params = this._getParams()
        params.token = this.token
        this.triggerExport(params)
      },
      onWarningModalCancelClick () {
        this.onCountCheckEnd()
        this.warningModalVisible = false
      },
      _getParams () {
        return R.merge(formatFilters([this.topForm]), formatFilters(this.filters))
      },
      getEventCount (params) {
        this.onCountCheckStart()
        return new Promise((resolve, reject) => {
          EventsAPI.count(cleanApiParams(params), params.token).then(data => {
            resolve(data.count)
          }).catch(error => {
            reject(error)
          })
        })
      },
      onAPIError (error) {
        this.exportInProgress = false
        this.gotAPIError = true
        error.response.json().then(response => {
          if (response.hasOwnProperty('error')) {
            this.errorMessage = response.error
          }
        })
      },
      onOverflowError () {
        this.exportInProgress = false
        this.gotOverflowError = true
      },
      onCountCheckStart () {
        this.countCheckInProgress = true
      },
      onCountCheckEnd () {
        this.countCheckInProgress = false
      },
      onExportStart () {
        this.gotAPIError = false
        this.gotOverflowError = false
        this.exportInProgress = true
      },
      onExportEnd () {
        this.exportInProgress = false
      }
    }
  }
</script>
